import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Button } from '@mui/material';

import { offerQueryKeys } from '~/hooks/apis/offers/useGetOffer';

import OffersService from '../../../../services/OffersService';
import { reportError } from '../../../../utils/reportError';

const UNDO_DELAY_MS = 5000;

interface UseUndoableDeleteOptions {
  offerId: string;
  /** Human-readable label for the snackbar message, e.g. "All 3 schedules for LE" */
  label: string;
  /** Returns the list of schedule IDs to delete. Evaluated at confirm-time so it
   *  always uses the latest schedules without needing them in a dependency array. */
  getIdList: () => Array<number>;
  /** Called after the API call succeeds */
  onSuccess: () => void;
  /** Called if the API call fails */
  onError: (error: Error) => void;
}

/**
 * Encapsulates the undo-snackbar pattern for destructive bulk-delete actions.
 *
 * Returns `{ confirm, isDeleting }`:
 * - `confirm()` – call when the user clicks the final "Delete" button in a confirmation dialog.
 *   It immediately dismisses the dialog (via the returned `isDeleting` flag), shows a 5-second
 *   undo snackbar, and – if the user doesn't click Undo – optimistically removes the schedules
 *   from the React Query cache before firing the API call.
 * - `isDeleting` – true while the undo window is open or the API call is in flight.
 */
export function useUndoableDelete({ offerId, label, getIdList, onSuccess, onError }: UseUndoableDeleteOptions) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }
    };
  }, []);

  const confirm = useCallback(() => {
    const idList = getIdList();
    if (idList.length === 0) {
      return;
    }

    setIsDeleting(true);

    const snackbarKey = enqueueSnackbar(`${label} deleted`, {
      variant: 'info',
      action: (key) => (
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
            closeSnackbar(key);
            setIsDeleting(false);
          }}
        >
          Undo
        </Button>
      ),
      autoHideDuration: UNDO_DELAY_MS,
    });

    undoTimerRef.current = setTimeout(() => {
      closeSnackbar(snackbarKey);
      queryClient.setQueryData(offerQueryKeys.detail(offerId), (old: App.Offer | undefined) => {
        if (!old) {
          return old;
        }
        return { ...old, brand_schedules: old.brand_schedules.filter((s) => !idList.includes(s.id)) };
      });
      OffersService.deleteScheduleList(offerId, { idList })
        .then(() => {
          setIsDeleting(false);
          onSuccess();
        })
        .catch((e) => {
          setIsDeleting(false);
          onError(e);
          reportError(e);
        });
    }, UNDO_DELAY_MS);
  }, [offerId, label, getIdList, onSuccess, onError, enqueueSnackbar, closeSnackbar, queryClient]);

  return { confirm, isDeleting };
}

interface UseUndoableCallbackOptions {
  /** Human-readable label for the snackbar message */
  label: string;
  /** The async action to execute after the undo window expires */
  onConfirm: () => Promise<void>;
}

/**
 * Lighter variant for cases where the caller provides its own async action
 * (e.g. the global "Clear Schedules" button that calls migrateScheduleData).
 */
export function useUndoableCallback({ label, onConfirm }: UseUndoableCallbackOptions) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }
    };
  }, []);

  const confirm = useCallback(() => {
    setIsDeleting(true);

    const snackbarKey = enqueueSnackbar(label, {
      variant: 'info',
      action: (key) => (
        <Button
          size="small"
          color="inherit"
          onClick={() => {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
            closeSnackbar(key);
            setIsDeleting(false);
          }}
        >
          Undo
        </Button>
      ),
      autoHideDuration: UNDO_DELAY_MS,
    });

    undoTimerRef.current = setTimeout(async () => {
      closeSnackbar(snackbarKey);
      await onConfirm();
      setIsDeleting(false);
    }, UNDO_DELAY_MS);
  }, [label, onConfirm, enqueueSnackbar, closeSnackbar]);

  return { confirm, isDeleting };
}
